import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, Mail, Pencil, Phone, Building2, CalendarClock, X, Camera, UserCheck, UserX, Users } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { COMPANY, US_STATES } from "../../constants/company";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const HOURS = Array.from({ length: 27 }, (_, i) => {
  const h = 7 + Math.floor(i / 2);
  return `${String(h).padStart(2, "0")}:${i % 2 === 0 ? "00" : "30"}`;
});

export default function Team() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [team, setTeam] = useState([]);
  const [invite, setInvite] = useState({ email: "", role: "staff" });
  const [invites, setInvites] = useState([]);
  const [inviting, setInviting] = useState(false);

  const load = () => {
    if (!isAdmin) return;
    api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
    api.get("/team/invites").then((r) => setInvites(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  if (!isAdmin) {
    return (
      <div data-testid="team-admin-only" className="flex flex-col items-center justify-center py-24 text-center animate-fade-up">
        <Users className="w-10 h-10 text-muted-foreground/40" />
        <h1 className="mt-4 text-2xl font-heading font-semibold">Admin access only</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-sm">The team directory is restricted to the office admin. Reach out to your administrator if you need team information.</p>
      </div>
    );
  }

  const deleteInvite = async (token) => {
    try {
      await api.delete(`/team/invites/${token}`);
      setInvites((l) => l.filter((i) => i.token !== token));
      toast.success("Invitation deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const clearInvites = async () => {
    try {
      await api.delete("/team/invites");
      setInvites([]);
      toast.success("All pending invitations cleared");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      await api.post("/team/invite", invite);
      toast.success(`Invitation emailed to ${invite.email}`);
      setInvite({ email: "", role: "staff" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setInviting(false);
    }
  };

  const toggleActive = async (m) => {
    try {
      await api.post(`/team/${m.user_id}/${m.is_active === false ? "activate" : "deactivate"}`);
      toast.success(m.is_active === false ? `${m.name} reactivated` : `${m.name} deactivated`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeMember = async (id, name) => {
    try {
      await api.delete(`/team/${id}`);
      toast.success(`${name} removed`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="team-page" className="space-y-8 animate-fade-up">
      <div>
        <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-medium">{COMPANY.shortName}</p>
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight mt-1">Our Team</h1>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="team-grid">
          {team.map((m) => {
            const canEdit = isAdmin || m.user_id === user?.user_id;
            return (
              <Card key={m.user_id} data-testid={`team-card-${m.user_id}`} className="hover:-translate-y-1 hover:shadow-md transition-transform duration-200">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    {m.photo_file_id ? (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${m.photo_file_id}`} alt={m.name}
                        className="w-12 h-12 rounded-md object-cover" data-testid={`team-photo-${m.user_id}`} />
                    ) : (
                      <div className="w-12 h-12 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-heading text-xl font-semibold">
                        {m.name?.charAt(0)?.toUpperCase()}
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Badge variant={m.role === "admin" ? "default" : "secondary"} className="capitalize" data-testid={`team-role-${m.user_id}`}>{m.role}</Badge>
                      {m.is_active === false && <Badge variant="destructive" data-testid={`team-inactive-${m.user_id}`}>Inactive</Badge>}
                      {isAdmin && m.role !== "admin" && (
                        <button onClick={() => toggleActive(m)} data-testid={`team-toggle-${m.user_id}`}
                          title={m.is_active === false ? "Reactivate account" : "Deactivate account"}
                          className="text-muted-foreground hover:text-primary transition-colors">
                          {m.is_active === false ? <UserCheck className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
                        </button>
                      )}
                      {isAdmin && m.role !== "admin" && (
                        <button onClick={() => removeMember(m.user_id, m.name)} data-testid={`team-remove-${m.user_id}`}
                          className="text-muted-foreground hover:text-destructive transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <h3 className="mt-4 font-heading text-xl font-semibold">{m.name}</h3>
                  {m.title && <p className="text-xs text-primary font-medium mt-0.5">{m.title}</p>}
                  <div className="mt-2 space-y-1">
                    <p className="text-xs text-muted-foreground flex items-center gap-1"><Mail className="w-3 h-3" /> {m.email}</p>
                    {m.phone && <p className="text-xs text-muted-foreground flex items-center gap-1"><Phone className="w-3 h-3" /> {m.phone}</p>}
                    {m.office && <p className="text-xs text-muted-foreground flex items-center gap-1"><Building2 className="w-3 h-3" /> {m.office}{m.state ? ` · ${m.state}` : ""}</p>}
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <CalendarClock className="w-3 h-3" />
                      {(m.availability?.work_days || [0, 1, 2, 3, 4]).map((d) => DAYS[d]).join(", ")} · {m.availability?.start_time || "09:00"}–{m.availability?.end_time || "17:00"}
                    </p>
                  </div>
                  {canEdit && <EditMemberDialog member={m} isAdmin={isAdmin} onSaved={load} />}
                </CardContent>
              </Card>
            );
          })}
        </div>
        {isAdmin && (
          <Card className="h-fit" data-testid="invite-member-card">
            <CardHeader><CardTitle className="font-heading text-xl flex items-center gap-2"><UserPlus className="w-5 h-5" /> Invite Team Member</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={sendInvite} className="space-y-4" data-testid="invite-member-form">
                <div className="space-y-1.5"><Label>Email address</Label><Input required type="email" placeholder="teammate@larontaprotaxservices.com" value={invite.email} onChange={(e) => setInvite((i) => ({ ...i, email: e.target.value }))} data-testid="invite-email-input" /></div>
                <div className="space-y-1.5">
                  <Label>Role</Label>
                  <Select value={invite.role} onValueChange={(v) => setInvite((i) => ({ ...i, role: v }))}>
                    <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="staff" data-testid="invite-role-staff">Team Member</SelectItem>
                      <SelectItem value="admin" data-testid="invite-role-admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full" disabled={inviting} data-testid="invite-submit">{inviting ? "Sending…" : "Send Email Invitation"}</Button>
                <p className="text-xs text-muted-foreground">They'll receive a branded email with a secure link to set up their password. The link never expires.</p>
              </form>
              {invites.length > 0 && (
                <div className="mt-6 pt-4 border-t border-border" data-testid="pending-invites-list">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-medium">Pending Invitations</p>
                    <Button type="button" size="sm" variant="ghost" className="text-destructive h-7 text-xs" onClick={clearInvites} data-testid="clear-invites-button">Clear all</Button>
                  </div>
                  <div className="space-y-2">
                    {invites.map((inv) => (
                      <div key={inv.token} className="flex items-center justify-between gap-2 text-sm" data-testid={`invite-row-${inv.email}`}>
                        <span className="truncate flex-1">{inv.email}</span>
                        <Badge variant="outline" className="capitalize shrink-0">{inv.role === "admin" ? "Admin" : "Team Member"}</Badge>
                        <button type="button" onClick={() => deleteInvite(inv.token)} title="Delete invitation" data-testid={`invite-delete-${inv.email}`}
                          className="text-muted-foreground hover:text-destructive transition-colors shrink-0">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function EditMemberDialog({ member, isAdmin, onSaved }) {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState({ name: member.name || "", title: member.title || "", office: member.office || "", phone: member.phone || "", email: member.email || "", bio: member.bio || "", role: member.role || "staff", state: member.state || "UT" });
  const [photoFile, setPhotoFile] = useState(null);
  const photoRef = useRef(null);
  const [av, setAv] = useState({
    work_days: member.availability?.work_days || [0, 1, 2, 3, 4],
    start_time: member.availability?.start_time || "09:00",
    end_time: member.availability?.end_time || "17:00",
    slot_minutes: member.availability?.slot_minutes || 30,
    blocked_dates: member.availability?.blocked_dates || [],
  });
  const [newBlocked, setNewBlocked] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const toggleDay = (d) => setAv((a) => ({ ...a, work_days: a.work_days.includes(d) ? a.work_days.filter((x) => x !== d) : [...a.work_days, d] }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/team/${member.user_id}`, info);
      await api.put(`/team/${member.user_id}/availability`, av);
      if (photoFile) {
        const fd = new FormData();
        fd.append("file", photoFile);
        await api.post(`/team/${member.user_id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Team member updated");
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const resetPassword = async () => {
    try {
      await api.post(`/team/${member.user_id}/reset-password`, { password: newPassword });
      toast.success("Password reset — share the new password with the team member");
      setNewPassword("");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setI = (k) => (e) => setInfo((i) => ({ ...i, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="mt-4 w-full" data-testid={`team-edit-${member.user_id}`}>
          <Pencil className="w-3.5 h-3.5 mr-1" /> Edit Info & Calendar
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid={`edit-dialog-${member.user_id}`}>
        <DialogHeader><DialogTitle className="font-heading text-2xl">Edit {member.name}</DialogTitle></DialogHeader>
        <div className="space-y-6 pt-2">
          <div className="space-y-3">
            <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-medium">Profile</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2"><Label>Full name</Label><Input value={info.name} onChange={setI("name")} data-testid="edit-member-name" /></div>
              <div className="space-y-1.5"><Label>Title</Label><Input placeholder="Tax Preparer" value={info.title} onChange={setI("title")} data-testid="edit-member-title" /></div>
              <div className="space-y-1.5"><Label>Office</Label><Input placeholder="Taylorsville Office" value={info.office} onChange={setI("office")} data-testid="edit-member-office" /></div>
              <div className="space-y-1.5">
                <Label>State (location)</Label>
                <Select value={info.state} onValueChange={(v) => setInfo((i) => ({ ...i, state: v }))}>
                  <SelectTrigger data-testid="edit-member-state"><SelectValue /></SelectTrigger>
                  <SelectContent>{US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Phone</Label><Input value={info.phone} onChange={setI("phone")} data-testid="edit-member-phone" /></div>
              {isAdmin && <div className="space-y-1.5"><Label>Email (login)</Label><Input type="email" value={info.email} onChange={setI("email")} data-testid="edit-member-email" /></div>}
              {isAdmin && (
                <div className="space-y-1.5">
                  <Label>Role</Label>
                  <Select value={info.role} onValueChange={(v) => setInfo((i) => ({ ...i, role: v }))}>
                    <SelectTrigger data-testid="edit-member-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="staff">Team Member</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Bio (shown on public team page)</Label>
              <Textarea rows={2} placeholder="Short professional bio…" value={info.bio} onChange={setI("bio")} data-testid="edit-member-bio" />
            </div>
            <div className="space-y-1.5">
              <Label>Profile photo</Label>
              <div className="flex items-center gap-3">
                <Button type="button" variant="outline" size="sm" onClick={() => photoRef.current?.click()} data-testid="edit-member-photo-button">
                  <Camera className="w-3.5 h-3.5 mr-1" /> {photoFile ? photoFile.name : "Upload photo"}
                </Button>
                <input ref={photoRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" data-testid="edit-member-photo-input" onChange={(e) => setPhotoFile(e.target.files?.[0] || null)} />
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-medium">Calendar availability</p>
            <div>
              <Label>Working days</Label>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {DAYS.map((d, i) => (
                  <label key={d} className={`flex items-center gap-1.5 border rounded-md px-2.5 py-1.5 text-xs cursor-pointer transition-colors ${av.work_days.includes(i) ? "border-primary bg-primary/10" : "border-border"}`} data-testid={`edit-day-${d.toLowerCase()}`}>
                    <Checkbox checked={av.work_days.includes(i)} onCheckedChange={() => toggleDay(i)} data-testid={`edit-day-checkbox-${d.toLowerCase()}`} /> {d}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Start</Label>
                <Select value={av.start_time} onValueChange={(v) => setAv((a) => ({ ...a, start_time: v }))}>
                  <SelectTrigger data-testid="edit-start-time"><SelectValue /></SelectTrigger>
                  <SelectContent>{HOURS.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>End</Label>
                <Select value={av.end_time} onValueChange={(v) => setAv((a) => ({ ...a, end_time: v }))}>
                  <SelectTrigger data-testid="edit-end-time"><SelectValue /></SelectTrigger>
                  <SelectContent>{HOURS.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Slot length</Label>
                <Select value={String(av.slot_minutes)} onValueChange={(v) => setAv((a) => ({ ...a, slot_minutes: Number(v) }))}>
                  <SelectTrigger data-testid="edit-slot-minutes"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 min</SelectItem>
                    <SelectItem value="30">30 min</SelectItem>
                    <SelectItem value="45">45 min</SelectItem>
                    <SelectItem value="60">60 min</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Blocked dates (days off)</Label>
              <div className="flex gap-2">
                <Input type="date" value={newBlocked} onChange={(e) => setNewBlocked(e.target.value)} data-testid="edit-blocked-date-input" />
                <Button type="button" variant="outline" size="sm" data-testid="edit-blocked-add"
                  onClick={() => { if (newBlocked && !av.blocked_dates.includes(newBlocked)) { setAv((a) => ({ ...a, blocked_dates: [...a.blocked_dates, newBlocked] })); setNewBlocked(""); } }}>
                  Block
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-1" data-testid="edit-blocked-list">
                {av.blocked_dates.map((d) => (
                  <span key={d} className="flex items-center gap-1 text-xs bg-muted rounded-md px-2 py-1" data-testid={`blocked-date-${d}`}>
                    {d}
                    <button type="button" onClick={() => setAv((a) => ({ ...a, blocked_dates: a.blocked_dates.filter((x) => x !== d) }))} data-testid={`blocked-remove-${d}`}>
                      <X className="w-3 h-3 text-muted-foreground hover:text-destructive" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          {isAdmin && (
            <div className="space-y-2 border-t border-border pt-4">
              <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-medium">Login access</p>
              <div className="flex gap-2">
                <Input type="password" minLength={6} placeholder="New password (min 6 chars)" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="edit-member-new-password" />
                <Button type="button" variant="outline" size="sm" disabled={newPassword.length < 6} onClick={resetPassword} data-testid="edit-member-reset-password">Reset</Button>
              </div>
            </div>
          )}

          <Button className="w-full" onClick={save} disabled={saving} data-testid="edit-member-save">
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
